import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { OperationService } from '../../../services/operations/operation/operation.service';
import {
  DailyLogType,
  DailyOperationLogRecord,
  DailyOperationLogRecordHelper,
  LogComment,
  TimeMaskOptions
} from '../../../models/projects/operation.models';
import { cloneDeep } from 'lodash';
import { CommentsService } from '../../../services/comments/comments.service';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { DateService } from '../../../services/date/date.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { LocalSaveService } from '../../../services/local/local-save.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { AppData } from '../../../models/app-data/app-data';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { LockedDayPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { AppConfig } from '../../../app/app.config';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';

@IonicPage()
@Component({
  selector: 'page-comment-edit',
  templateUrl: 'comment-edit.html',
})

export class CommentEditPage {
  config = AppConfig.settings;
  tempComment: LogComment;
  originalComment: LogComment;
  record: DailyOperationLogRecord;
  loading: boolean = true;
  isWeb: boolean;
  startTimeMask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  endTimeMask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  currentDate: PtLuxonDate;
  isValid: boolean = false;
  lockedDayPermissions: LockedDayPermissions;

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public operationService: OperationService,
              public navHelperService: NavHelperService,
              public commentsService: CommentsService,
              private toastCtrl: ToastController,
              private localSaveService: LocalSaveService,
              private appDataSyncService: AppDataSyncService,
              private timeZoneService: TimeZoneService,
              public dateService: DateService,
              public lockedDayService: LockedDayService,
              private dailyLogService: DailyLogService,
              private dateParserService: DateParserService,
              public platform: Platform,
              private permissionService: PermissionsService,
              private modalController: ModalController
  ) {
    this.record = navParams.get('record') ? navParams.get('record') : this.createCommentLogRecord();
    this.lockedDayPermissions = navParams.get('lockedDayPermissions');

    this.tempComment = {
      id: this.record.comment ? cloneDeep(this.record.comment.id) : null,
      data: this.record.comment ? cloneDeep(this.record.comment.data) : null,
      appear: this.record.comment ? cloneDeep(this.record.comment.appear) : null,
      milestone: this.record.comment ? cloneDeep(this.record.comment.milestone) : false,
      startTimeToShow: this.record.comment ? cloneDeep(this.record.comment.startTimeToShow) : null,
      endTimeToShow: this.record.comment ? cloneDeep(this.record.comment.endTimeToShow) : null,
      startTimeDate: this.record.comment ? cloneDeep(this.record.comment.startTimeDate) : null,
      startTimeOffset: this.record.comment && this.record.comment.startTimeDate && this.record.comment.startTimeDate.dateTime
        ? cloneDeep(this.record.comment.startTimeDate.dateTime.offset / 60)
        : null,
      endTimeOffset: this.record.comment && this.record.comment.endTimeDate && this.record.comment.endTimeDate.dateTime
        ? cloneDeep(this.record.comment.endTimeDate.dateTime.offset / 60)
        : null,
      endTimeDate: this.record.comment ? cloneDeep(this.record.comment.endTimeDate) : null,
      recordApiId: this.record.comment ? cloneDeep(this.record.comment.recordApiId) : null,
      recordLocalId: this.record.comment ? cloneDeep(this.record.comment.recordLocalId) : null,
      locked: false
    };

    this.originalComment = this.tempComment ? cloneDeep(this.tempComment) : {data: undefined, appear: undefined, milestone: false};
  }

  async ionViewDidEnter() {
    if (!this.lockedDayPermissions) {
      this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.currentDate);
    }

    this.currentDate = await this.dailyLogService.getCurrentLogDay();
    this.navHelperService.commentWarned = false;
    this.navHelperService.showCommentWarning = false;
    this.isWeb = this.platform.is('core');
    this.isValid = this.record.endTimeShow != null && this.record.endTimeShow !== ''
      || this.record.startTimeShow != null && this.record.startTimeShow !== '';
    this.loading = false;
  }

  ionViewCanLeave(): boolean {
    if (this.navHelperService.unsavedComment && !this.navHelperService.commentWarned) {
      this.navHelperService.commentWarned = true;
      this.navHelperService.showCommentWarning = true;
      this.navHelperService.unsavedComment = false;
      return false;
    }
    return true;
  }

  startChanged() {
    this.navHelperService.unsavedComment = true;
  }

  async startValueChanged() {
    const allowed = await this.dateService.timeAllowed(this.record.startTimeShow, this.record.startTime && this.record.startTime.dateTime || this.currentDate.dateTime);

    if (!allowed) {
      this.isValid = false;
      return;
    }

    const valid = this.dateService.timeIsValid(this.record.startTimeShow);

    if (!valid) {
      if (this.record.startTime && this.record.startTime.dateTime) {
        this.record.startTimeShow = await this.dateService.setTimeStringFromPtLuxonDate(this.record.startTime);
      } else {
        this.record.startTimeShow = '';
      }
    } else {
      this.record.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(this.record.startTimeShow, this.currentDate.dateTime);
    }

    if ((this.record.startTimeShow === '' || this.record.startTimeShow === null)
      || !this.timeIsValid(this.record.startTimeShow) && !this.timeIsValid(this.record.endTimeShow)) {
      this.isValid = false;
      return;
    }

    if (this.record.startTime && this.record.startTime.dateTime && this.record.startTime.isLostInTime && this.record.startTime.isLostInTime.result) {
      this.isValid = false;
      return;
    }

    this.isValid = true;
  }

  commentChanged(comment: any) {
    this.tempComment = comment;

    if (this.originalComment.data !== this.tempComment.data
      || this.originalComment.appear !== this.tempComment.appear
      || this.originalComment.milestone !== this.tempComment.milestone) {
      this.navHelperService.unsavedComment = true;
    } else {
      this.navHelperService.unsavedComment = false;
    }
  }

  async save() {
    this.record.comment = this.tempComment;
    this.record.comment.startTimeOffset = this.record.startTimeOffset;
    this.record.commentAppearOnDPR = this.tempComment.appear;
    const appDataBlob = new AppData();

    this.operationService
      .getDailyLogRecordTempId().then(async id => {
      const helper: DailyOperationLogRecordHelper =
        cloneDeep(await this.commentsService.createCommentLogEntry(this.record, this.record.id ? this.record.id : id));

      appDataBlob.dailyLogRecords = helper.records;

      try {
        await this.appDataSyncService.saveAppData(appDataBlob);
      } catch (error) {
        this.loading = false;
        throw (error);
      }

      this.navHelperService.unsavedComment = false;

      this.presentSaveToast().then(() => {
        this.navCtrl.setRoot(DailyLogPage);
        this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
      });
    });
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Comment Saved',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  timeIsValid(time: string): boolean {
    switch (time) {
      case '':
      case null:
      case undefined:
        return false;
    }

    return true;
  }

  createCommentLogRecord(): DailyOperationLogRecord {
    const record: DailyOperationLogRecord = {
      id: null,
      projectSpecificOperationApiId: null,
      setupChangeLogApiId: null,
      guid: '',
      isContractual: false,
      contractualConcurrentText: '',
      operationClassification: null,
      isInterruption: false,
      lineTypeName: 'Comment',
      startTime: null,
      startTimeShow: null,
      endTime: null,
      endTimeShow: null,
      startTimeOffset: null,
      endTimeOffset: null,
      comment: null,
      commentAppearOnDPR: null,
      stepInstance: null,
      operationName: null,
      operationInstanceId: null,
      stageIds: null,
      sortTime: null,
      typeId: DailyLogType.Comment,
      selected: false,
      selectedSwap: false,
      selectedSwapHighlight: false,
      disabled: false,
      startTimeConfirmed: false,
      endTimeConfirmed: false,
      ordinal: 18,
      uniqueOrdinal: null,
      stepNumber: null,
      commentIdentifier: null,
      operationStartTime: null,
      archived: false,
      secondaryGuid: null,
      canConfirmTimes: true,
      customSort: null
    };

    return record;
  }

  configureStartTimeRegex() {
    if (this.record.startTimeShow || this.record.endTimeShow) {
      const minHour0 = 0;
      const maxHour0 = this.timeNullOrEmpty(this.record.endTimeShow) ? 2 : this.getDigit(this.record.endTimeShow, 0);

      this.startTimeMask[0] = new RegExp(`[${minHour0}` + '-' + `${maxHour0}]`);
      this.startTimeMask[1] = new RegExp('\\d');
      this.startTimeMask[3] = new RegExp(`[0-5]`);
      this.startTimeMask[4] = new RegExp('\\d');
    }
  }

  configureEndTimeRegex() {
    if (this.record.startTimeShow || this.record.endTimeShow) {
      const minHour0 = this.timeNullOrEmpty(this.record.startTimeShow) ? 0 : this.getDigit(this.record.startTimeShow, 0);
      const maxHour0 = 2;

      this.endTimeMask[0] = new RegExp(`[${minHour0}` + '-' + `${maxHour0}]`);
      this.endTimeMask[1] = new RegExp('\\d');
      this.endTimeMask[3] = new RegExp(`[0-5]`);
      this.endTimeMask[4] = new RegExp('\\d');
    }
  }

  // Gets called whenever the user changes a start or end time to configure the correct regex pattern.
  // Abandon all hope, ye who enter here.
  regexChange(timeEntered: string, startChanged: boolean) {
    if (this.timeNullOrEmpty(timeEntered)) {
      if (startChanged) {
        this.configureStartTimeRegex();
      } else {
        this.configureEndTimeRegex();
      }
      return;
    }

    const timeMask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
    const min = !this.timeNullOrEmpty(this.record.startTimeShow) && !this.record.startTimeShow.includes('_')
      ? this.record.startTimeShow
      : '00:00';
    const max = !this.timeNullOrEmpty(this.record.endTimeShow) && !this.record.endTimeShow.includes('_')
      ? this.record.endTimeShow
      : '23:59';
    const digits = this.calculateEnteredDigits(timeEntered);

    timeMask[1] = this.configureTimeMask({
      minDigit: Number.parseInt(min.charAt(0).toString(), 10),
      maxDigit: Number.parseInt(max.charAt(0).toString(), 10),
      digitCombo: digits[0],
      totalMaxDigit: 9,
      position: 1,
    }, min, max);

    timeMask[3] = this.configureTimeMask({
      minDigit: Number.parseInt(min.charAt(0).toString() + min.charAt(1).toString(), 10),
      maxDigit: Number.parseInt(max.charAt(0).toString() + max.charAt(1).toString(), 10),
      digitCombo: Number.parseInt(digits[0].toString() + digits[1].toString(), 10),
      totalMaxDigit: 5,
      position: 3,
    }, min, max);

    timeMask[4] = this.configureTimeMask({
      minDigit: Number.parseInt(min.charAt(0).toString() + min.charAt(1).toString() + min.charAt(3).toString(), 10),
      maxDigit: Number.parseInt(max.charAt(0).toString() + max.charAt(1).toString() + max.charAt(3).toString(), 10),
      digitCombo: Number.parseInt(digits[0].toString() + digits[1].toString() + digits[2].toString(), 10),
      totalMaxDigit: 9,
      position: 4,
    }, min, max);


    let tempSecondDigitUpperBounds = '9';

    if (max.charAt(0) === min.charAt(0) || timeEntered.charAt(0) === max.charAt(0)) {
      tempSecondDigitUpperBounds = max.charAt(1);
    } else if (timeEntered.charAt(0) === min.charAt(0)) {
      if (max.charAt(0) > min.charAt(0)) {
        tempSecondDigitUpperBounds = '9';
      } else {
        tempSecondDigitUpperBounds = max.charAt(1);
      }
    }

    if (startChanged) {
      this.startTimeMask = timeMask;
    } else {
      this.endTimeMask = timeMask;
    }
  }

  private configureTimeMask(options: TimeMaskOptions, min: string, max: string): RegExp {

    if (options.minDigit === options.maxDigit) {
      return new RegExp(`[${min.charAt(options.position)}` + '-' + `${max.charAt(options.position)}]`);
    }

    if (options.minDigit === options.digitCombo) {
      return new RegExp(`[${min.charAt(options.position)}` + '-' + `${options.totalMaxDigit}]`);
    }

    if (options.maxDigit === options.digitCombo) {
      return new RegExp(`[0` + '-' + `${max.charAt(options.position)}]`);
    }

    return new RegExp(`[0-${options.totalMaxDigit}]`);
  }

  private calculateEnteredDigits(timeEnter: string): number[] {
    const numbers = [];
    numbers.push(Number.parseInt(timeEnter.charAt(0), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(1), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(3), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(4), 10));
    return numbers;
  }

  private getDigit(time: string, arrayPosition: number): number {
    return Number(time.charAt(arrayPosition));
  }

  private timeNullOrEmpty(time: string): boolean {
    if (time === undefined || time === null || time === '') {
      return true;
    }

    return false;
  }

  async offsetSelected(ptLuxonDate: PtLuxonDate) {
    this.record.startTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/comments/comment-edit/comment-edit.ts